import { Trans } from '@lingui/react/macro';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { BrandSitePanel } from '../components/brand-site-panel.js';
import { TopNav } from '../components/top-nav.js';
import type { fetchMe } from '../lib/api.js';
import { useBrandTitle } from '../lib/document-title.js';
import { brandsQuery, meQuery } from '../lib/queries.js';

// /brand/$slug/site — the founder's Magig-hosted marketing site. Mag
// drafts it from the brain (archetype-matched structure + voice-true
// copy); this surface is where the founder generates it, previews the
// rendered page, and regenerates. Hosting at <handle>.magig.site is a
// later phase — for now this is the in-app draft + preview.

export const Route = createFileRoute('/brand/$slug/site')({
  beforeLoad: async ({ params, context: { queryClient } }) => {
    const me = await queryClient.ensureQueryData(meQuery());
    if (!me) throw redirect({ to: '/login' });
    const brands = await queryClient.ensureQueryData(brandsQuery());
    const brand = brands.find((b) => b.slug === params.slug);
    if (!brand) throw redirect({ to: '/brands' });
    return { me, brand };
  },
  loader: ({ context }) =>
    context as {
      me: Awaited<ReturnType<typeof fetchMe>>;
      brand: { id: string; name: string; slug: string; siteHandle: string | null };
    },
  component: SitePage,
});

function SitePage() {
  const { brand } = Route.useLoaderData();
  useBrandTitle(brand.slug, ['site']);

  return (
    <main className="min-h-screen bg-[var(--cream)]">
      <TopNav active={null} />
      <section className="mx-auto max-w-[900px] px-4 pb-16 pt-8 sm:px-7">
        <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
          <Trans>↘ {brand.name.toLowerCase()} · site</Trans>
        </p>
        <h1 className="mt-2 font-display text-[28px] font-black leading-[1.05] tracking-[-0.04em] sm:text-[34px]">
          <Trans>your marketing site, drafted by mag.</Trans>
        </h1>
        <p className="mt-3 max-w-[680px] text-[14.5px] leading-[1.5] text-[var(--ink)]/70">
          <Trans>
            mag reads your brain, picks a page structure proven for your kind of business, and
            writes the whole thing in your voice. generate it, look it over, regenerate until it
            feels right.
          </Trans>
        </p>

        <div className="mt-8">
          <BrandSitePanel brand={brand} />
        </div>
      </section>
    </main>
  );
}
